'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';

export interface ProcessStep {
  step: string;
  title: string;
  description: string;
}

export interface ProcessStepsProps {
  steps: ProcessStep[];
  sectionTitle?: string;
  sectionSubtitle?: string;
  sectionDescription?: string;
  showBottomCta?: boolean;
  bottomCtaText?: string;
  className?: string;
}

/**
 * ProcessSteps - An interactive accordion component for displaying process/workflow steps
 * 
 * Features:
 * - Interactive accordion with smooth expand/collapse
 * - Minimal design with stroke-only icons and circles
 * - Space-efficient with spectacular animations
 * - Fully responsive (mobile & desktop optimized)
 * - Click-to-expand functionality
 * 
 * Inspired by Linear, Figma, and modern interactive designs
 */
export function ProcessSteps({
  steps,
  sectionTitle = "How We Work",
  sectionSubtitle = "Our Approach",
  sectionDescription = "A proven methodology designed to deliver exceptional results, every time.",
  showBottomCta = true,
  bottomCtaText = "Ready to see these steps in action?",
  className = "",
}: ProcessStepsProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className={`relative py-24 lg:py-32 px-4 sm:px-6 lg:px-8 bg-white ${className}`}>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16 lg:mb-20"
        >
          <p className="text-xs font-medium tracking-wider text-gray-400 uppercase mb-3">
            {sectionSubtitle}
          </p>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl font-light text-gray-900 mb-4">
            {sectionTitle}
          </h2>
          <p className="text-base lg:text-lg text-gray-500 max-w-2xl font-light">
            {sectionDescription}
          </p>
        </motion.div>

        {/* Interactive Accordion */}
        <div className="relative">
          {/* Desktop: Horizontal Tabs + Content */}
          <div className="hidden lg:block">
            {/* Tab Navigation */}
            <div className="flex items-center justify-center gap-3 mb-12">
              {steps.map((step, index) => (
                <motion.button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className="relative group"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {/* Circle with stroke */}
                  <div className={`relative w-16 h-16 rounded-full border-2 transition-all duration-300 flex items-center justify-center ${
                    activeIndex === index 
                      ? 'border-gray-900 bg-gray-900' 
                      : 'border-gray-300 bg-white hover:border-gray-400'
                  }`}>
                    <span className={`text-sm font-semibold transition-colors duration-300 ${
                      activeIndex === index ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'
                    }`}>
                      {step.step}
                    </span>
                  </div>
                  
                  {/* Step title below circle */}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 whitespace-nowrap">
                    <p className={`text-xs font-medium transition-colors duration-300 ${
                      activeIndex === index ? 'text-gray-900' : 'text-gray-400'
                    }`}>
                      {step.title}
                    </p>
                  </div>

                  {/* Connecting line */}
                  {index < steps.length - 1 && (
                    <div className="absolute top-1/2 left-full w-3 h-px bg-gray-200" />
                  )}
                </motion.button>
              ))}
            </div>

            {/* Content Area */}
            <div className="relative mt-20 min-h-[200px]">
              <AnimatePresence mode="wait">
                {steps[activeIndex] && (
                  <motion.div
                    key={activeIndex}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4, ease: [0.21, 0.47, 0.32, 0.98] }}
                    className="relative"
                  >
                    <div className="bg-gray-50 border border-gray-100 rounded-3xl p-10 lg:p-12">
                      {/* Large step number watermark */}
                      <div className="absolute top-8 right-8 opacity-[0.03]">
                        <span className="text-[120px] font-bold text-gray-900 leading-none">
                          {steps[activeIndex].step}
                        </span>
                      </div>

                      <div className="relative">
                        <div className="flex items-start gap-4 mb-6">
                          <div className="w-14 h-14 rounded-full border-2 border-gray-900 flex items-center justify-center flex-shrink-0">
                            <span className="text-lg font-semibold text-gray-900">
                              {steps[activeIndex].step}
                            </span>
                          </div>
                          <div>
                            <h3 className="text-3xl font-semibold text-gray-900 mb-2">
                              {steps[activeIndex].title}
                            </h3>
                          </div>
                        </div>
                        <p className="text-lg text-gray-600 leading-relaxed max-w-2xl">
                          {steps[activeIndex].description}
                        </p>
                      </div>

                      {/* Navigation arrows */}
                      <div className="flex items-center gap-3 mt-10">
                        <button
                          onClick={() => setActiveIndex(Math.max(0, activeIndex - 1))}
                          disabled={activeIndex === 0}
                          className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:border-gray-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setActiveIndex(Math.min(steps.length - 1, activeIndex + 1))}
                          disabled={activeIndex === steps.length - 1}
                          className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:border-gray-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                        <span className="text-sm text-gray-400 ml-2">
                          {activeIndex + 1} / {steps.length}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Mobile: Vertical Accordion */}
          <div className="lg:hidden space-y-3">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <button
                  onClick={() => setActiveIndex(activeIndex === index ? -1 : index)}
                  className="w-full text-left group"
                >
                  <div className={`border rounded-2xl transition-all duration-300 ${
                    activeIndex === index 
                      ? 'border-gray-300 bg-gray-50' 
                      : 'border-gray-100 bg-white hover:border-gray-200'
                  }`}>
                    {/* Header */}
                    <div className="p-5 flex items-center gap-4">
                      {/* Circle */}
                      <div className={`w-12 h-12 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                        activeIndex === index 
                          ? 'border-gray-900 bg-gray-900' 
                          : 'border-gray-300 bg-white'
                      }`}>
                        <span className={`text-sm font-semibold transition-colors duration-300 ${
                          activeIndex === index ? 'text-white' : 'text-gray-400'
                        }`}>
                          {step.step}
                        </span>
                      </div>

                      {/* Title */}
                      <h3 className={`text-lg font-semibold flex-1 transition-colors duration-300 ${
                        activeIndex === index ? 'text-gray-900' : 'text-gray-600'
                      }`}>
                        {step.title}
                      </h3>

                      {/* Expand icon */}
                      <div className={`w-6 h-6 flex items-center justify-center transition-transform duration-300 ${
                        activeIndex === index ? 'rotate-180' : ''
                      }`}>
                        <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>

                    {/* Expandable content */}
                    <AnimatePresence>
                      {activeIndex === index && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: [0.21, 0.47, 0.32, 0.98] }}
                          className="overflow-hidden"
                        >
                          <div className="px-5 pb-5 pt-2">
                            <p className="text-sm text-gray-600 leading-relaxed">
                              {step.description}
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </button>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        {showBottomCta && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-12 text-center"
          >
            <p className="text-sm text-gray-400">
              {bottomCtaText}
            </p>
          </motion.div>
        )}
      </div>
    </section>
  );
}

export default ProcessSteps;
